import PropTypes from 'prop-types';
import { action, extendObservable } from 'mobx';
import { PropTypes as MobxPropTypes } from 'mobx-react';
import AsyncActionStore from '../AsyncActionStore';
import RoleAccessModalStore from './RoleAccessModalStore';
import { ALL_ROLES } from '../Administrators/AdministratorModalStore';
import { apiCall } from '../../API';
import { emailValidator } from '../../email';

export default class RoleAccessStore {
    static PropType = PropTypes.shape({
        groups: MobxPropTypes.arrayOrObservableArray.isRequired,
        roles: MobxPropTypes.arrayOrObservableArrayOf(PropTypes.shape({
            value: PropTypes.string.isRequired,
            groups: MobxPropTypes.arrayOrObservableArray.isRequired,
        })).isRequired,

        roleModal: RoleAccessModalStore.PropType.isRequired,
        listStatus: AsyncActionStore.PropType.isRequired,
        updateStatus: AsyncActionStore.PropType.isRequired,

        load: PropTypes.func.isRequired,
        searchGroups: PropTypes.func.isRequired,
        updateGroups: PropTypes.func.isRequired,
    });

    constructor(data = { possibleRoles: ALL_ROLES }) {
        extendObservable(this, /** @class RoleAccessStore */{
            groups: [],
            roles: data.possibleRoles.map((role) => ({ value: role.value, groups: [] })),
            currentSearchSuccessCount: 0,


            apiGroupList: apiCall('/console/groups/all'),
            apiGetRoleGroupsMap: apiCall('/console/groups/get'),
            apiUpdateRoleGroupsMap: apiCall('/console/groups/set'),
            apiSearchRoleGroups: apiCall('/console/groups/search'),

            roleModal: new RoleAccessModalStore(),
            listStatus: new AsyncActionStore(),
            searchStatus: new AsyncActionStore(),
            updateStatus: new AsyncActionStore(),
        }, data);
    }

    openModal = action(() => {
        this.roleModal.setValue({ groups: this.groups, roles: this.roles });
        this.roleModal.modal.open();
    });

    load = action(() => {
        if (!this.listStatus.isLoading) {
            this.listStatus.setLoading(Promise.all([this.apiGroupList(), this.apiGetRoleGroupsMap()])
                .then(([groups, roleGroupsMap]) => {
                    groups = groups || [];
                    roleGroupsMap = roleGroupsMap || {};
                    this.roles.forEach((role) => {
                        const roleGroups = roleGroupsMap[role.value] || [];
                        roleGroups.forEach((roleGroup) => {
                            const suitableGroup = groups.find((checkedGroup) => checkedGroup.id === roleGroup.id);
                            roleGroup.unsync = !suitableGroup || suitableGroup.unsync;
                            if (!suitableGroup) {
                                groups.push({ ...roleGroup });
                            }
                        });
                        this.sortRoleGroups(roleGroups);
                        role.groups.replace(roleGroups);
                    });
                    this.sortRoleGroups(groups);
                    this.groups.replace(groups);
                }));
        }
        return this.listStatus.promise;
    });

    searchGroups = action((role, event) => {
        role.groupsSelect.filter.onChangeHandler(event);

        const value = event.target.value;
        if (!emailValidator(value)) {
            this.searchStatus.setBase();
            role.groupsSelect.setFilterValues(null);
            return Promise.resolve();
        }

        this.currentSearchSuccessCount = 0;
        this.searchStatus.setLoading(this.apiSearchRoleGroups({ value })).then((groups) => {
            if (emailValidator(role.groupsSelect.filter.value)) {
                if (value === role.groupsSelect.filter.value) {
                    this.currentSearchSuccessCount = this.searchStatus.successCount;
                    role.groupsSelect.setFilterValues(groups.map((group) => group.id));
                } else if (!this.currentSearchSuccessCount ||
                    this.searchStatus.successCount < this.currentSearchSuccessCount) {
                    this.searchStatus.isLoading = true;
                }
                const newGroups =
                    groups.filter((group) => this.groups.every((checkedGroup) => checkedGroup.id !== group.id));
                if (newGroups.length > 0) {
                    this.groups.push(...newGroups);
                    this.roleModal.updateGroups(role, this.groups);
                }
            }
        });
        return this.searchStatus.promise;
    });

    updateGroups = action(() => {
        if (!this.updateStatus.isLoading) {
            const rolesData = this.roleModal.getValue();

            const roleGroupsMap = rolesData.roles
                .reduce((currentMap, role) => ({ ...currentMap, [role.value]: role.groups }), {});
            this.updateStatus.setLoading(this.apiUpdateRoleGroupsMap(roleGroupsMap).then(action(() => {
                this.roles.replace([...rolesData.roles]);
                this.roles.forEach((role) => role.groups.replace(this.sortRoleGroups(role.groups)));
                this.roleModal.modal.close();
                return rolesData;
            })));
        }
        return this.updateStatus.promise;
    });

    sortRoleGroups = (roleGroups) => {
        return roleGroups.sort((group1, group2) => {
            if (group1.unsync !== group2.unsync) {
                return group1.unsync ? -1 : 1;
            }
            return String(group1.name).toLowerCase().localeCompare(String(group2.name).toLowerCase());
        });
    };
}
